import { useState, useEffect } from 'react'
import { Container, Card, Button, Modal, Form, Alert, Badge, ListGroup, Tabs, Tab, ProgressBar } from 'react-bootstrap'
import { useProfile } from '../contexts/ProfileContext'
import profileService from '../services/profileService'
import networkService from '../services/networkService'

function Trust() {
  const { activeProfile, profiles } = useProfile()
  const [trustRelationships, setTrustRelationships] = useState([])
  const [availableProfiles, setAvailableProfiles] = useState([])
  const [availableNetworks, setAvailableNetworks] = useState([])

  const [showAddProfileModal, setShowAddProfileModal] = useState(false)
  const [showAddNetworkModal, setShowAddNetworkModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingTrust, setEditingTrust] = useState(null)

  const [profileFormData, setProfileFormData] = useState({
    target_profile_id: '',
    category_name: '',
    trust: 50,
    description: ''
  })

  const [networkFormData, setNetworkFormData] = useState({
    network_id: '',
    category_name: '',
    trust: 50,
    description: ''
  })

  const [editFormData, setEditFormData] = useState({
    trust: 50,
    name: '',
    description: ''
  })

  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (activeProfile) {
      loadTrustRelationships()
      loadAvailableProfiles()
      loadAvailableNetworks()
    }
  }, [activeProfile])

  const loadTrustRelationships = async () => {
    if (!activeProfile) return

    try {
      const data = await profileService.getTrustRelationships(activeProfile.id)
      setTrustRelationships(data)
    } catch (err) {
      console.error('Failed to load trust relationships:', err)
    }
  }

  const loadAvailableProfiles = async () => {
    try {
      const allProfiles = await profileService.getProfiles()
      // Filter out current profile
      const others = allProfiles.filter(p => p.id !== activeProfile?.id)
      setAvailableProfiles(others)
    } catch (err) {
      console.error('Failed to load profiles:', err)
    }
  }

  const loadAvailableNetworks = async () => {
    try {
      const networks = await networkService.getNetworks()
      setAvailableNetworks(networks)
    } catch (err) {
      console.error('Failed to load networks:', err)
    }
  }

  const handleAddProfileTrust = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      await profileService.trustProfile(activeProfile.id, {
        target_profile_id: parseInt(profileFormData.target_profile_id),
        category_name: profileFormData.category_name,
        trust: parseInt(profileFormData.trust),
        description: profileFormData.description || undefined
      })

      setSuccess('Trust relationship created successfully!')
      setShowAddProfileModal(false)
      setProfileFormData({ target_profile_id: '', category_name: '', trust: 50, description: '' })
      loadTrustRelationships()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create trust relationship')
    } finally {
      setLoading(false)
    }
  }

  const handleAddNetworkTrust = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      await profileService.trustNetwork(activeProfile.id, {
        network_id: parseInt(networkFormData.network_id),
        category_name: networkFormData.category_name,
        trust: parseInt(networkFormData.trust),
        description: networkFormData.description || undefined
      })

      setSuccess('Network trust relationship created successfully!')
      setShowAddNetworkModal(false)
      setNetworkFormData({ network_id: '', category_name: '', trust: 50, description: '' })
      loadTrustRelationships()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create trust relationship')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateTrust = async (e) => {
    e.preventDefault()
    if (!editingTrust) return

    setLoading(true)
    setError(null)

    try {
      await profileService.updateTrustRelationship(activeProfile.id, editingTrust.id, {
        trust: parseInt(editFormData.trust),
        name: editFormData.name,
        description: editFormData.description
      })

      setSuccess('Trust relationship updated successfully!')
      setShowEditModal(false)
      setEditingTrust(null)
      loadTrustRelationships()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update trust relationship')
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveTrust = async (trust) => {
    if (!confirm(`Are you sure you want to remove this trust relationship?`)) return

    try {
      const targetType = trust.target_type === 'profile' ? 'profile' : 'network'
      const targetId = trust.scoring_profile_id || trust.scoring_network_id

      await profileService.untrust(activeProfile.id, targetType, targetId)
      setSuccess('Trust relationship removed successfully!')
      loadTrustRelationships()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove trust relationship')
    }
  }

  const openEditModal = (trust) => {
    setEditingTrust(trust)
    setEditFormData({
      trust: trust.trust,
      name: trust.name,
      description: trust.description || ''
    })
    setShowEditModal(true)
  }

  const getTrustColor = (trust) => {
    if (trust >= 80) return 'success'
    if (trust >= 50) return 'info'
    if (trust >= 30) return 'warning'
    return 'danger'
  }

  const profileTrusts = trustRelationships.filter(t => t.target_type === 'profile')
  const networkTrusts = trustRelationships.filter(t => t.target_type === 'network')

  const getProfileName = (profileId) => {
    const profile = availableProfiles.find(p => p.id === profileId)
    return profile?.name || `Profile ${profileId}`
  }

  const getNetworkName = (networkId) => {
    const network = availableNetworks.find(n => n.id === networkId)
    return network?.name || `Network ${networkId}`
  }

  return (
    <Container className="mt-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <div>
          <h2>Trust Management</h2>
          <p className="text-muted mb-0">
            Build your trust network to personalize search results
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button
            variant="primary"
            onClick={() => setShowAddProfileModal(true)}
            disabled={!activeProfile}
          >
            Trust Profile
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => setShowAddNetworkModal(true)}
            disabled={!activeProfile}
          >
            Trust Network
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      {!activeProfile ? (
        <Alert variant="warning">
          Please select or create a profile to manage trust relationships.
        </Alert>
      ) : (
        <Tabs defaultActiveKey="profiles" className="mb-3">
          <Tab eventKey="profiles" title={`Trusted Profiles (${profileTrusts.length})`}>
            {profileTrusts.length === 0 ? (
              <Card className="text-center py-4">
                <Card.Body>
                  <p className="text-muted">You haven't trusted any profiles yet.</p>
                  <Button variant="primary" onClick={() => setShowAddProfileModal(true)}>
                    Trust Your First Profile
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <ListGroup>
                {profileTrusts.map((trust) => (
                  <ListGroup.Item key={trust.id}>
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2 mb-2">
                      <div className="flex-grow-1 w-100">
                        <h6 className="mb-1">
                          {getProfileName(trust.scoring_profile_id)}
                          <Badge bg="secondary" className="ms-2">{trust.name}</Badge>
                        </h6>
                        {trust.description && (
                          <small className="text-muted d-block mb-2">{trust.description}</small>
                        )}
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1 me-3">
                            <ProgressBar
                              now={trust.trust}
                              label={`${trust.trust}%`}
                              variant={getTrustColor(trust.trust)}
                            />
                          </div>
                          <Badge bg={getTrustColor(trust.trust)}>{trust.trust}</Badge>
                        </div>
                      </div>
                      <div className="d-flex gap-2 mt-2 mt-md-0 flex-shrink-0">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => openEditModal(trust)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveTrust(trust)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Tab>

          <Tab eventKey="networks" title={`Trusted Networks (${networkTrusts.length})`}>
            {networkTrusts.length === 0 ? (
              <Card className="text-center py-4">
                <Card.Body>
                  <p className="text-muted">You haven't trusted any networks yet.</p>
                  <Button variant="primary" onClick={() => setShowAddNetworkModal(true)}>
                    Trust Your First Network
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <ListGroup>
                {networkTrusts.map((trust) => (
                  <ListGroup.Item key={trust.id}>
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2 mb-2">
                      <div className="flex-grow-1 w-100">
                        <h6 className="mb-1">
                          {getNetworkName(trust.scoring_network_id)}
                          <Badge bg="secondary" className="ms-2">{trust.name}</Badge>
                        </h6>
                        {trust.description && (
                          <small className="text-muted d-block mb-2">{trust.description}</small>
                        )}
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1 me-3">
                            <ProgressBar
                              now={trust.trust}
                              label={`${trust.trust}%`}
                              variant={getTrustColor(trust.trust)}
                            />
                          </div>
                          <Badge bg={getTrustColor(trust.trust)}>{trust.trust}</Badge>
                        </div>
                      </div>
                      <div className="d-flex gap-2 mt-2 mt-md-0 flex-shrink-0">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => openEditModal(trust)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveTrust(trust)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Tab>
        </Tabs>
      )}

      {/* Add Profile Trust Modal */}
      <Modal show={showAddProfileModal} onHide={() => setShowAddProfileModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Trust a Profile</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddProfileTrust}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Profile *</Form.Label>
              <Form.Select
                value={profileFormData.target_profile_id}
                onChange={(e) => setProfileFormData({...profileFormData, target_profile_id: e.target.value})}
                required
              >
                <option value="">Select a profile to trust...</option>
                {availableProfiles.map(profile => (
                  <option key={profile.id} value={profile.id}>{profile.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Category Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Science, Education, Entertainment"
                value={profileFormData.category_name}
                onChange={(e) => setProfileFormData({...profileFormData, category_name: e.target.value})}
                required
              />
              <Form.Text className="text-muted">
                Category helps organize trust relationships
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Trust Level: {profileFormData.trust}%</Form.Label>
              <Form.Range
                min="0"
                max="100"
                value={profileFormData.trust}
                onChange={(e) => setProfileFormData({...profileFormData, trust: e.target.value})}
              />
              <div className="d-flex justify-content-between">
                <small className="text-muted">Low Trust</small>
                <small className="text-muted">High Trust</small>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Why do you trust this profile in this category?"
                value={profileFormData.description}
                onChange={(e) => setProfileFormData({...profileFormData, description: e.target.value})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddProfileModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Trust'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Add Network Trust Modal */}
      <Modal show={showAddNetworkModal} onHide={() => setShowAddNetworkModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Trust a Network</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddNetworkTrust}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Network *</Form.Label>
              <Form.Select
                value={networkFormData.network_id}
                onChange={(e) => setNetworkFormData({...networkFormData, network_id: e.target.value})}
                required
              >
                <option value="">Select a network to trust...</option>
                {availableNetworks.map(network => (
                  <option key={network.id} value={network.id}>
                    {network.name} {network.verified && '✓'}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Category Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Science, Education, Entertainment"
                value={networkFormData.category_name}
                onChange={(e) => setNetworkFormData({...networkFormData, category_name: e.target.value})}
                required
              />
              <Form.Text className="text-muted">
                Category helps organize trust relationships
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Trust Level: {networkFormData.trust}%</Form.Label>
              <Form.Range
                min="0"
                max="100"
                value={networkFormData.trust}
                onChange={(e) => setNetworkFormData({...networkFormData, trust: e.target.value})}
              />
              <div className="d-flex justify-content-between">
                <small className="text-muted">Low Trust</small>
                <small className="text-muted">High Trust</small>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Why do you trust this network in this category?"
                value={networkFormData.description}
                onChange={(e) => setNetworkFormData({...networkFormData, description: e.target.value})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddNetworkModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Trust'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Trust Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Trust Relationship</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateTrust}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Category Name *</Form.Label>
              <Form.Control
                type="text"
                value={editFormData.name}
                onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Trust Level: {editFormData.trust}%</Form.Label>
              <Form.Range
                min="0"
                max="100"
                value={editFormData.trust}
                onChange={(e) => setEditFormData({...editFormData, trust: e.target.value})}
              />
              <div className="d-flex justify-content-between">
                <small className="text-muted">Low Trust</small>
                <small className="text-muted">High Trust</small>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={editFormData.description}
                onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Trust'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  )
}

export default Trust
